import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  Avatar,
  IconButton,
  InputAdornment,
  Tabs,
  Tab
} from '@mui/material';
import { Visibility, VisibilityOff, Edit } from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useModal } from '../../contexts/ModalContext';

const MyProfile = () => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const { openSuccessModal, openErrorModal, closeModal } = useModal();
  // Edit Profile state
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    mobile: '',
    employee_id: '',
    role_verbose: '',
    team: '',
    country_code: ''
  });
  const { user, setUser, logout } = useAuth();
  const [profileErrors, setProfileErrors] = useState({});
  const [isEditingProfile, setIsEditingProfile] = useState(false);

  // Change Password state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Load user data on component mount
  useEffect(() => {
    setProfileData({
      name: user.name || '',
      email: user.email || '',
      mobile: user.mobile || '',
      employee_id: user.employee_id || '',
      role_verbose: user.role_verbose || '',
      team: user.team || '',
      country_code: user.country_code || ''
    });
    setLoading(false);
    console.log('user ', user);
    // eslint-disable-next-line 
  }, [user]);


  // Handle profile data changes
  const handleProfileChange = (field) => (event) => {
    setProfileData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    // Clear error when user starts typing
    if (profileErrors[field]) {
      setProfileErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Handle password data changes
  const handlePasswordChange = (field) => (event) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    // Clear error when user starts typing
    if (passwordErrors[field]) {
      setPasswordErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Toggle password visibility
  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Validate profile data
  const validateProfile = () => {
    const errors = {};
    if (!profileData.name.trim()) errors.name = 'Name is required';
    if (!profileData.mobile.trim()) errors.mobile = 'Mobile is required';
    if (!profileData.employee_id.trim()) errors.employee_id = 'Employee ID is required';
    if (!profileData.country_code.trim()) errors.country_code = 'Country Code is required';

    setProfileErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Validate password data
  const validatePassword = () => {
    const errors = {};
    if (!passwordData.current_password) {
      errors.current_password = 'Current password is required';
    }
    if (!passwordData.new_password) {
      errors.new_password = 'New password is required';
    } else if (passwordData.new_password.length < 8) {
      errors.new_password = 'Password must be at least 8 characters';
    }
    if (!passwordData.confirm_password) {
      errors.confirm_password = 'Please confirm your password';
    } else if (passwordData.new_password !== passwordData.confirm_password) {
      errors.confirm_password = 'Passwords do not match';
    }
    if (
      passwordData.current_password &&
      passwordData.new_password &&
      passwordData.current_password === passwordData.new_password
    ) {
      errors.new_password = 'New password cannot be the same as the current password';
    }

    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Update profile
  const handleUpdateProfile = async () => {
    if (!validateProfile()) return;
    setLoading(true);
    try {
      // Only send name and mobile for update
      const updateData = {
        name: profileData.name,
        mobile: profileData.mobile,
        employee_id: profileData.employee_id,
        country_code: profileData.country_code
      };
      const response = await api.patch('user-profile/', updateData);
      setUser({ ...user, ...response.data });
      setProfileData(prev => ({ ...prev, ...response.data }));
      setIsEditingProfile(false);
      openSuccessModal('Profile updated successfully!');
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      const errorData = error.response?.data;
      if (errorData) {
        setProfileErrors(errorData);
      } else {
        openErrorModal(
          'Error',
          'Failed to update profile. Please try again.',
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Change password
  const handleChangePassword = async () => {
    if (!validatePassword()) return;
    
    setLoading(true);
    try {
      const response = await api.post('change-password/', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      console.log('response ', response);
      if(response.status === 200) {
        openSuccessModal(
          'Password Changed',
          'Password changed successfully!',
          () => {
            logout();
          }
        );
      } else {
        openErrorModal(
          'Error',
          'Failed to change password. Please try again.',
        );
      }

      setPasswordData({
        new_password: '',
        confirm_password: ''
      });

      // Clear message after 3 seconds
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      const errorData = error.response?.data;
      if (errorData) {
        setPasswordErrors(errorData);
      } else {
        openErrorModal(
          'Error',
          'Failed to change password. Please try again.',
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Cancel profile editing
  const handleCancelProfile = () => {
    setProfileData({
      name: user.name || '',
      email: user.email || '',
      mobile: user.mobile || '',
      employee_id: user.employee_id || '',
      role_verbose: user.role_verbose || '',
      team: user.team || '',
      country_code: user.country_code || ''
    });
    setProfileErrors({});
    setIsEditingProfile(false);
  };

  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 4 }}>
        My Profile
      </Typography>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {message.text && (
            <Alert severity={message.type} sx={{ mb: 3 }}>
              {message.text}
            </Alert>
          )}
          <Box sx={{ display: 'flex', minHeight: 400 }}>
            <Tabs
              orientation="vertical"
              value={tab}
              onChange={(e, v) => setTab(v)}
              sx={{ borderRight: 1, borderColor: 'divider', minWidth: 180 }}
            >
              <Tab label="Profile" />
              <Tab label="Change Password" />
            </Tabs>
            <Box sx={{ flex: 1, p: 3 }}>
              {tab === 0 && (
                <Card elevation={2} sx={{ maxWidth: 600 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                      <Typography variant="h6" component="h2">
                        Edit Profile
                      </Typography>
                      {!isEditingProfile && (
                        <IconButton 
                          onClick={() => setIsEditingProfile(true)}
                          color="primary"
                          size="small"
                        >
                          <Edit />
                        </IconButton>
                      )}
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Name"
                          value={profileData.name}
                          onChange={handleProfileChange('name')}
                          error={Boolean(profileErrors.name)}
                          helperText={profileErrors.name}
                          disabled={!isEditingProfile || loading}
                          margin="normal"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Email"
                          type="email"
                          value={profileData.email}
                          onChange={handleProfileChange('email')}
                          error={Boolean(profileErrors.email)}
                          helperText={profileErrors.email}
                          disabled={true}
                          margin="normal"
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          fullWidth
                          label="Country Code"
                          value={profileData.country_code}
                          onChange={handleProfileChange('country_code')}
                          error={Boolean(profileErrors.country_code)}
                          helperText={profileErrors.country_code}
                          disabled={!isEditingProfile || loading}
                          margin="normal"
                        />
                      </Grid>
                      <Grid item xs={9}>
                        <TextField
                          fullWidth
                          label="Mobile"
                          value={profileData.mobile}
                          onChange={handleProfileChange('mobile')}
                          error={Boolean(profileErrors.mobile)}
                          helperText={profileErrors.mobile}
                          disabled={!isEditingProfile || loading}
                          margin="normal"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Employee ID"
                          value={profileData.employee_id}
                          onChange={handleProfileChange('employee_id')}
                          error={Boolean(profileErrors.employee_id)}
                          helperText={profileErrors.employee_id}
                          disabled={!isEditingProfile || loading}
                          margin="normal"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Role"
                          value={profileData.role_verbose}
                          disabled
                          margin="normal"
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Team"
                          value={profileData.team}
                          disabled
                          margin="normal"
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                    </Grid>
                    {isEditingProfile && (
                      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                        <Button
                          variant="contained"
                          onClick={handleUpdateProfile}
                          disabled={loading}
                          startIcon={loading ? <CircularProgress size={20} /> : null}
                        >
                          {loading ? 'Updating...' : 'Update Profile'}
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={handleCancelProfile}
                          disabled={loading}
                        >
                          Cancel
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}
              {tab === 1 && (
                <Card elevation={2} sx={{ maxWidth: 600 }}>
                  <CardContent>
                    <Typography variant="h6" component="h2" gutterBottom sx={{ mb: 3 }}>
                      Change Password
                    </Typography>
                    <TextField
                      fullWidth
                      label="Current Password"
                      type={showPasswords.current ? 'text' : 'password'}
                      value={passwordData.current_password}
                      onChange={handlePasswordChange('current_password')}
                      error={Boolean(passwordErrors.current_password)}
                      helperText={passwordErrors.current_password}
                      disabled={loading}
                      margin="normal"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => togglePasswordVisibility('current')}
                              edge="end"
                            >
                              {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      fullWidth
                      label="New Password"
                      type={showPasswords.new ? 'text' : 'password'}
                      value={passwordData.new_password}
                      onChange={handlePasswordChange('new_password')}
                      error={Boolean(passwordErrors.new_password)}
                      helperText={passwordErrors.new_password}
                      disabled={loading}
                      margin="normal"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => togglePasswordVisibility('new')}
                              edge="end"
                            >
                              {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Confirm New Password"
                      type={showPasswords.confirm ? 'text' : 'password'}
                      value={passwordData.confirm_password}
                      onChange={handlePasswordChange('confirm_password')}
                      error={Boolean(passwordErrors.confirm_password)}
                      helperText={passwordErrors.confirm_password}
                      disabled={loading}
                      margin="normal"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => togglePasswordVisibility('confirm')}
                              edge="end"
                            >
                              {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Box sx={{ mt: 3 }}>
                      <Button
                        variant="contained"
                        onClick={handleChangePassword}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : null}
                        fullWidth
                      >
                        {loading ? 'Changing Password...' : 'Change Password'}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default MyProfile; 